/**
 * API Response Validation
 *
 * Uses @vhire/contracts to validate API responses at runtime.
 * This catches API changes/bugs before they cause frontend errors.
 *
 * Benefits:
 * - Validates data shape matches contract
 * - Provides clear error messages when API breaks
 * - Type-safe data after validation
 *
 * @example
 * ```typescript
 * const response = await fetchApi('/jobs');
 * const jobs = validateArray(response.data?.jobs, JobListingSchema, 'jobs');
 * ```
 */

import { z, type ZodError } from 'zod';
import type { ZodSchema } from 'zod';

/**
 * Validation error with context
 */
export class ApiValidationError extends Error {
  constructor(
    public readonly endpoint: string,
    public readonly field: string,
    public readonly zodError: ZodError
  ) {
    const issues = zodError.issues.map(i => `${i.path.join('.')}: ${i.message}`).join(', ');
    super(`API validation failed for ${endpoint}/${field}: ${issues}`);
    this.name = 'ApiValidationError';
  }
}

/**
 * Validate a single value against a schema
 * Returns the validated data or null on failure
 */
export function validateSafe<T>(
  data: unknown,
  schema: ZodSchema<T>
): { success: true; data: T } | { success: false; error: ZodError } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

/**
 * Validate a single value against a schema
 * Throws ApiValidationError on failure
 */
export function validate<T>(
  data: unknown,
  schema: ZodSchema<T>,
  endpoint: string,
  field: string
): T {
  const result = schema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  throw new ApiValidationError(endpoint, field, result.error);
}

/**
 * Validate an array of values against a schema
 * Returns empty array on failure (with console warning)
 */
export function validateArray<T>(
  data: unknown,
  schema: ZodSchema<T>,
  endpoint: string,
  field: string
): T[] {
  if (!Array.isArray(data)) {
    // eslint-disable-next-line no-console
    console.warn(`[API] Expected array for ${endpoint}/${field}, got:`, typeof data);
    return [];
  }

  const arraySchema = z.array(schema);
  const result = arraySchema.safeParse(data);

  if (result.success) {
    return result.data;
  }

  // Log validation issues but return partial data
  // eslint-disable-next-line no-console
  console.warn(`[API] Validation issues for ${endpoint}/${field}:`, result.error.issues);

  // Try to return items that pass validation
  return data
    .map((item, index) => {
      const itemResult = schema.safeParse(item);
      if (itemResult.success) {
        return itemResult.data;
      }
      // eslint-disable-next-line no-console
      console.warn(`[API] Item ${index} failed validation:`, itemResult.error.issues[0]);
      return null;
    })
    .filter((item): item is T => item !== null);
}

/**
 * Validate a complete array without manufacturing an empty or partial result.
 *
 * Candidate-facing inventories must use this variant: a missing array or one
 * malformed row means the upstream answer is unavailable, not that the
 * remaining rows are a truthful complete listing. The caller decides how to
 * represent that unavailable state; this function never returns partial data.
 */
export function validateCompleteArray<T>(
  data: unknown,
  schema: ZodSchema<T>,
  endpoint: string,
  field: string,
): { success: true; data: T[] } | { success: false; error: ZodError } {
  const result = z.array(schema).safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }

  // eslint-disable-next-line no-console
  console.warn(`[API] Complete-array validation failed for ${endpoint}/${field}:`, result.error.issues);
  return { success: false, error: result.error };
}

const publicJobsCursorSchema = z.object({
  afterTs: z.number().int().nonnegative(),
  afterId: z.string().trim().min(1).max(128),
});

const publicJobsPaginationSchema = z.object({
  total: z.number().int().nonnegative().nullable(),
  limit: z.number().int().positive().max(100),
  offset: z.number().int().nonnegative().nullable(),
  hasMore: z.boolean(),
  next: publicJobsCursorSchema.nullable(),
}).superRefine((pagination, ctx) => {
  if (pagination.hasMore && pagination.next === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['next'],
      message: 'A continuing page requires a cursor',
    });
  }
  if (!pagination.hasMore && pagination.next !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['next'],
      message: 'A terminal page must not advertise a cursor',
    });
  }
});

export type ValidPublicJobsPagination = z.infer<typeof publicJobsPaginationSchema>;

/**
 * Validate the public inventory continuation contract atomically.
 *
 * Coercing a missing boolean to `false`, an invalid cursor to `null`, or a
 * missing total to `null` can turn upstream drift into a factual empty result
 * or silently truncate the marketplace. The caller must treat failure as
 * unavailability, exactly like a malformed jobs array.
 */
export function validatePublicJobsPagination(
  data: unknown,
): { success: true; data: ValidPublicJobsPagination } | { success: false; error: ZodError } {
  const result = publicJobsPaginationSchema.safeParse(data);
  if (result.success) return { success: true, data: result.data };

  // eslint-disable-next-line no-console
  console.warn('[API] Public jobs pagination validation failed:', result.error.issues);
  return { success: false, error: result.error };
}

/**
 * Validate with fallback - returns default value on validation failure
 */
export function validateWithDefault<T>(
  data: unknown,
  schema: ZodSchema<T>,
  defaultValue: T,
  endpoint: string,
  field: string
): T {
  const result = schema.safeParse(data);
  if (result.success) {
    return result.data;
  }

  // eslint-disable-next-line no-console
  console.warn(`[API] Using default for ${endpoint}/${field} due to validation failure`);
  return defaultValue;
}

/**
 * Check if validation would succeed (without throwing)
 */
export function isValid<T>(data: unknown, schema: ZodSchema<T>): data is T {
  return schema.safeParse(data).success;
}
